import { Controller, useWatch } from "react-hook-form";
import { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";

import { Autocomplete, Box, Chip, TextField } from "@mui/material";
import { getEquipmentsByCommaKeyword } from "./axios/equipmentAxios";
import { useEnumApi } from "contexts/EnumApiContext";
import { useTranslation } from "react-i18next";

const EquipmentAutocomplete = ({control, name}) => {
    const { t } = useTranslation();
    const { getEnumValueByEnumNameAndCode } = useEnumApi();

    const [options, setOptions] = useState([]);
    const [inputValue, setInputValue] = useState(""); // 현재 타이핑 중인 텍스트 상태
    const [loading, setLoading] = useState(false);

    const watchedValue = useWatch({
        control,
        name
    });

    useEffect(() => {
        if (watchedValue) {
            setInputValue(
                `${watchedValue.equipmentName || ""}`
            );
        } else {
            setInputValue("");
        }
    }, [watchedValue]);

    // 1. API 호출 함수 (컴마로 구분된 전체 텍스트를 인자로 받음)
    const fetchApi = useCallback(
        debounce(async (fullText) => {
            if (fullText) {
                const fullQuery = fullText.join(',').trim();
                if (!fullQuery) {
                    setOptions([]);
                    return;
                }
                setLoading(true);
                try {
                    // console.log("전송될 AND 검색어:", fullQuery);
                    const res = await getEquipmentsByCommaKeyword({keywords: `${fullQuery}`})
                    setOptions(res || []);
                } finally {
                    setLoading(false);
                }
            }
        }, 400),
        []
    );

    // 엔터 및 콤마 입력을 감지하여 검색을 트리거하는 함수
    const handleSearchTrigger = (currentValue) => {
        const keywords = currentValue.split(',').map(s => s.trim()).filter(Boolean);
        fetchApi(keywords);
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={{ required: t('message.common.alert.msg.select.target.equipment') }}
            defaultValue={null}
            render={({ field: { onChange, value }, fieldState }) => (
                <Autocomplete
                    options={options || []}
                    filterOptions={(options) => {
                        // console.log("filterOptions", options);
                        return options;
                    }}
                    value={value || null}
                    inputValue={inputValue}
                    loading={loading}
                    noOptionsText={t('message.common.alert.placeholder.search.enter.or.comma')}
                    loadingText={t('message.common.text.msg.loading.data')}

                    // 타이핑 중에는 상태만 업데이트 (API 호출 X)
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input' || reason === 'clear') {
                            setInputValue(newInputValue);
                            if (reason === 'clear') {
                                setOptions([]);
                            }
                        }
                    }}

                    // 목록에서 선택했을 때
                    onChange={(event, newValue) => {
                        // console.log(newValue)
                        onChange(newValue); 
                        if (newValue) {
                            setInputValue(`${newValue.equipmentName || ''}`);
                        } else {
                            setInputValue("");
                        }
                    }}

                    getOptionLabel={(option) => {
                        if (typeof option === 'string') return option;
                        return option ? `${option.equipmentName || ''}` : "";
                    }}

                    isOptionEqualToValue={(option, value) => option.equipmentId === value?.equipmentId}

                    renderOption={(props, option) => {
                        const { key, ...optionProps } = props;
                        return (
                        <Box 
                            component="li" 
                            key={key} 
                            {...optionProps} 
                            sx={{ 
                                borderBottom: '1px solid #1e293b', 
                                py: 1, 
                                display: 'flex', 
                                flexDirection: 'column',
                                alignItems: 'flex-start' 
                            }}
                        >
                            <Box sx={{ fontSize: '12px', color: '#64748b', mb: 0.5 }}>
                                {getEnumValueByEnumNameAndCode('SubwayLine', option.subwayLine)?.[0]?.title}
                            </Box>
                            <Box sx={{ fontSize: '14px', color: '#e2e8f0', fontWeight: 'bold' }}>
                                {option.equipmentName} ({option.equipmentNo})
                            </Box>
                        </Box>
                        );
                    }}

                    renderInput={(params) => (
                        <TextField
                        {...params}
                        label={t('message.common.text.title.search.target.equipment')}
                        placeholder={value ? "" : t('message.common.text.placeholder.search.short')}
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message || t('message.common.text.msg.search.guide.enter.or.comma')}
                        
                        // 💡 핵심: 특정 키 입력 시점에만 백엔드 호출 트리거
                        onKeyDown={(event) => {
                            // 한글 글자 조합 중(엔터 중복 방지)일 때는 통과
                            if (event.nativeEvent.isComposing) return;

                            if (event.key === 'Enter') {
                                // Autocomplete 기본 엔터 동작(폼 서브밋 등) 방지
                                event.preventDefault(); 
                                handleSearchTrigger(inputValue);
                            }
                            
                            if (event.key === ',') {
                            // 콤마 기호가 텍스트에 들어가기 직전 혹은 직후에 호출 처리
                            // event.currentTarget.value는 입력 직전 값이므로 현재 inputValue에 콤마를 붙여 미리 예측 파싱하거나 
                            // 타임아웃을 살짝 주어 입력된 직후의 값을 보냅니다.
                            setTimeout(() => {
                                handleSearchTrigger(event.target.value);
                            }, 10);
                            }
                        }}

                        InputProps={{
                            ...params.InputProps,
                            startAdornment: value && value.equipmentId ? (
                            <Chip
                                icon={<span className="icon_png"><img src={`${process.env.PUBLIC_URL}/img/icon/check_circle.png`} alt='' /></span>}
                                label={t('message.common.text.label.selected')}
                                color="success"
                                size="small"
                                sx={{ mr: 1, backgroundColor: '#064e3b', color: '#34d399', fontWeight: 'bold' }}
                                onDelete={() => {
                                    onChange(null);
                                    setInputValue("");
                                    setOptions([]);
                                }}
                            />
                            ) : params.InputProps.startAdornment,
                        }}
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                backgroundColor: "#0f172a",
                                color: "#e2e8f0",
                            }
                        }}
                        />
                    )}
                />
            )}
        />
    );
}

export default EquipmentAutocomplete;